export default {
  index: {
    title: '潮汐與潮流指南',
    description:
      '了解潮汐與潮流的運作原理：潮汐係數、漲潮流與退潮流、潮汐基準面，以及潮汐預報如何計算。',
    subtitle: '以簡短淺白的文字，說明潮汐與潮流預報背後的科學。',
  },
  back: '返回所有指南',
  guides: {
    'tidal-coefficients': {
      title: '潮汐係數解說',
      description:
        '潮汐係數的意義：20 到 120 的尺度、大潮與小潮如何與月球相關，以及係數愈高為何潮差愈大。',
      blurb: '以 20 到 120 的尺度概括潮汐的大小，以及月球如何驅動它。',
      body: [
        {
          p: '潮汐係數是用單一數字概括某一天潮汐大小的指標。法國以及承襲法國水文測量傳統的國家廣泛採用。此係數為無因次，通常介於約 20 到 120 之間：數字愈大，滿潮與乾潮之間的落差（潮差）愈大，相伴的潮流也愈強。',
        },
        { h2: '20 到 120 的尺度' },
        {
          p: '此係數將當天的潮差與長期參考值相比較。尺度上的幾個數值有約定俗成的意義：',
        },
        {
          list: [
            { term: '20', text: '最小的潮汐，接近天文潮差的最小值。' },
            { term: '45', text: '平均的小潮，潮差較小時。' },
            { term: '70', text: '平均潮，大致位於小潮與大潮之間。' },
            { term: '95', text: '平均的大潮，潮差較大時。' },
            { term: '120', text: '最大的潮汐，接近天文潮差的最大值。' },
          ],
        },
        { h2: '係數為何變化：月球' },
        {
          p: '係數依約兩週的大潮-小潮週期起伏。係數高的大潮出現在每次新月與滿月之後一兩天，此時太陽與月球連成一線，兩者的引潮力相加。係數低的小潮出現在上弦月與下弦月前後，此時太陽與月球成直角，力量部分抵消。',
        },
        {
          p: '月球的距離也有影響。當大潮與近地點（月球最接近地球之處）重合時，係數達到最高值。最大的潮汐往往集中在春分與秋分前後。',
        },
        { h2: '係數無法告訴你的事' },
        {
          p: '係數只描述天文潮差，不包含天氣的影響：風與氣壓可以在不受係數影響的情況下抬升或降低實際水位。此外，係數是當天整體潮汐的性質，而非任一地點以公尺或英尺表示的高度。',
        },
      ],
    },
    'tidal-currents': {
      title: '漲潮流、退潮流與憩流：潮流如何運作',
      description:
        '潮流如何運作：漲潮流與退潮流的方向、轉流時的憩流、以節表示的流速與流向（set），以及憩流為何不一定與滿潮、乾潮同步。',
      blurb: '漲潮流、退潮流與憩流，以及最強的流不一定出現在滿潮時的原因。',
      body: [
        {
          p: '潮流是伴隨潮汐垂直升降而產生的水體水平運動。潮位告訴你水有多深，潮流則告訴你水朝哪個方向、以多快的速度流動。潮流在水道、灣口與河口最為重要，那裡的流速可達數節。',
        },
        { h2: '漲潮流、退潮流與憩流' },
        {
          list: [
            {
              term: '漲潮流',
              text: '朝岸邊或溯河口而上流動的水，一般發生在潮位上升時。',
            },
            {
              term: '退潮流',
              text: '朝外海或順河口而下流動的水，一般發生在潮位下降時。',
            },
            {
              term: '憩流',
              text: '當流在漲潮流與退潮流之間轉向時，流速接近零的短暫停頓。',
            },
          ],
        },
        {
          p: '流速以節（每小時海里）計量。其方向稱為流向（set），以羅盤方位表示，即水流去的方向。',
        },
        { h2: '憩流不一定在滿潮時' },
        {
          p: '常見的假設是，流會在滿潮與乾潮時停止。這只在部分地點成立。時間點取決於潮波在當地如何表現。在潮汐表現為進行波之處，例如狹長開闊的水道，最強的漲潮流與退潮流出現在滿潮與乾潮附近，而憩流落在中間點附近。在潮汐表現為駐波之處，例如封閉海灣的灣頂，情形相反：流速最快在半潮附近，而憩流出現在滿潮與乾潮附近。當地的潮流表才是可靠的依據。',
        },
        { h2: '反向流與旋轉流' },
        {
          p: '在狹窄水道中，流通常是反向流：漲潮時朝一個方向流動，接著轉向並在退潮時反向流回。在開闊的沿岸海域，流往往是旋轉流，在一個潮汐週期內沿羅盤方位連續改變方向，而非沿單一直線反向。',
        },
      ],
    },
    'tidal-datums': {
      title: '潮汐基準面與潮位',
      description:
        '潮位以什麼為基準測量：海圖基準面、最低天文潮位（LAT）、平均低低潮位（MLLW），以及各國基準面為何不同。',
      blurb: '海圖基準面、LAT、MLLW，以及潮位數字實際代表的意義。',
      body: [
        {
          p: '每個潮位都是相對於一個稱為基準面的參考高度的測量值。當潮汐表說水位會達到 1.8 公尺，指的是高於當地潮汐基準面 1.8 公尺：不是高於海床，也不是日常意義上的海平面。知道基準面，高度才有意義。',
        },
        { h2: '什麼是潮汐基準面' },
        {
          p: '潮汐基準面是由潮汐的某個特定階段所定義的參考高程，並經長期平均：依慣例約為 19 年，以平滑月球軌道中 18.6 年的週期。由於它是實際觀測值的平均，基準面因地點以及計算它的機構而異。',
        },
        { h2: '海圖基準面與常見參考面' },
        {
          p: '海圖採用低潮基準面，使實際水深幾乎總是不低於海圖標示的水深。此海圖基準面即是計算海圖水深與潮位的零點。各國採用不同的基準面：',
        },
        {
          list: [
            {
              term: 'LAT（最低天文潮位）',
              text: '在平均天氣下可預測的最低水位；由英國及許多其他國家採用。',
            },
            {
              term: 'MLLW（平均低低潮位）',
              text: '每日兩次乾潮中較低者的平均；美國的標準海圖基準面。',
            },
            {
              term: 'MSL（平均海平面）',
              text: '長期平均水位，主要作為陸地高程的參考，而非用於海圖。',
            },
          ],
        },
        {
          p: '平均高潮位（MHW）與平均高高潮位（MHHW）等高潮基準面則用於相反的目的，例如界定海岸線或橋樑淨空。',
        },
        { h2: '為何高度無法直接比較' },
        {
          p: '由於基準面不同，除非兩者採用相同的參考面，否則一個測站的高度無法直接與另一個測站的高度相比較。以 LAT 測得的 0.5 公尺乾潮，與以 MLLW 測得的 0.5 公尺乾潮並非相同的水位。這也是為什麼同一個實際潮汐，在不同國家的潮汐表中會以不同數字描述。',
        },
      ],
    },
    'how-tide-predictions-work': {
      title: '潮汐預報如何計算',
      description:
        '潮汐如何預報：月球與太陽的重力驅動、M2 與 S2 等調和分潮，以及觀測水位為何與天文預報不同。',
      blurb: '調和分析、構成潮汐的分潮，以及天氣為何使實際情況有所不同。',
      body: [
        {
          p: '潮汐之所以能提前多年預報，是因為它由月球與太陽的運動所驅動，而這些運動已被極精確地掌握。某地的潮汐是海洋對該重力驅動的反應，並受當地海岸線與水深的形塑。將天文轉化為潮汐表的方法稱為調和分析。',
        },
        { h2: '潮汐作為分潮的總和' },
        {
          p: '調和分析將潮汐視為許多簡單波的總和，每一個都稱為一個分潮。每個分潮的頻率由天文決定：取決於地球、月球與太陽彼此如何運動：而其振幅與時相則透過將分潮擬合到該地點一段實際水位觀測資料而求得。主要的分潮包括：',
        },
        {
          list: [
            { term: 'M2', text: '主要太陰半日潮，週期 12.42 小時；通常最大。' },
            { term: 'S2', text: '主要太陽半日潮，週期 12.00 小時。' },
            { term: 'N2', text: '源自月球橢圓軌道的太陰分潮。' },
            {
              term: 'K1 and O1',
              text: '主要的全日潮分潮，在許多地方使一天中的一次滿潮大於另一次。',
            },
          ],
        },
        {
          p: '熟悉的大潮-小潮週期由此自然浮現：M2 與 S2 的週期略有不同，因此逐漸錯開又對齊，每 14.8 天相互增強而形成大潮，部分抵消而形成小潮。',
        },
        { h2: '向前預報' },
        {
          p: '一旦知道某地點各分潮的振幅與相位，預報潮汐便只是計算任一未來時刻該總和的問題。天文頻率永不改變，因此計算是確定性的，可推算至數十年之後。',
        },
        { h2: '實際潮汐為何不同' },
        {
          p: '預報只是天文潮汐，不包含天氣。強風、低氣壓或高氣壓、豪雨與河川逕流，都可能抬升或降低實際水位：暴潮可使水位高出預報一公尺以上，而高氣壓與離岸風則可使水位遠低於預報。因此，預報是掌握潮汐時間與大小的絕佳依據，但當天觀測到的水位可能有所不同。',
        },
      ],
    },
  },
}
